import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Descriptions,
  Empty,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ChevronLeft, Edit3, ExternalLink, Plus, ShieldOff, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { iamApi as iamHttpApi, type IamPolicyGrantRecord } from '@/api/iam.api';
import { PolicyPermissionView } from '@/pages/iam/components/PolicyPermissionView';
import { useRevokePolicyGrant } from '@/pages/iam/composables/useGrantPolicyData';
import { useDeleteIamPolicy } from '@/pages/iam/composables/useIamSupportData';
import { usePolicyPermissionItems } from '@/pages/iam/composables/usePolicyPermissionItems';
import { debounce, fmtTime } from '@/utils/utils';
import GrantPolicyDrawer from './components/GrantPolicyDrawer';
import './policies.css';

/**
 * 聚焦和断线重连不自动刷新，避免用户停留时产生额外请求；重新进入详情页则必须刷新，
 * 以免复用上一次挂载留下的策略和授权记录。
 */
const POLICY_DETAIL_QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: 'always',
  staleTime: 0,
} as const;

/** IAM 策略时间戳以秒为单位；仅在展示时转换，不改写接口数据。 */
function formatPolicyTime(timestamp: number): string {
  return fmtTime(new Date(timestamp * 1000).toISOString());
}

export default function PolicyDetail() {
  const { message, modal } = App.useApp();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: policyResponse,
    error: policyError,
    isLoading: isPolicyLoading,
  } = useQuery({
    queryKey: ['iam', 'policies', 'detail', id] as const,
    queryFn: async () => {
      const response = await iamHttpApi.getPolicy(id);
      if (response.code !== 0) {
        throw new Error(response.message || '获取权限策略详情失败');
      }
      return response;
    },
    enabled: !!id,
    ...POLICY_DETAIL_QUERY_OPTIONS,
  });
  const policy = policyResponse?.data;
  const {
    data: grantsResponse,
    error: grantsError,
    isLoading: areGrantsLoading,
  } = useQuery({
    queryKey: ['iam', 'policies', 'grants', id] as const,
    queryFn: async () => {
      const response = await iamHttpApi.searchPolicyGrants({
        limit: 100,
        offset: '0',
        policy_id: id,
      });
      if (response.code !== 0) {
        throw new Error(response.message || '查询策略授权记录失败');
      }
      return response;
    },
    enabled: !!id,
    ...POLICY_DETAIL_QUERY_OPTIONS,
  });
  const deletePolicy = useDeleteIamPolicy();
  const revokePolicyGrant = useRevokePolicyGrant();

  const permissionItems = usePolicyPermissionItems({
    actions: policy?.permission_summary.actions,
  });
  const grants = useMemo(() => grantsResponse?.data.records ?? [], [grantsResponse]);

  const [showSource, setShowSource] = useState(false);
  const [grantSearch, setGrantSearch] = useState('');
  const [grantQuery, setGrantQuery] = useState('');
  const [grantDrawerOpen, setGrantDrawerOpen] = useState(false);
  const [checkingPolicyId, setCheckingPolicyId] = useState<string>();

  const updateGrantQuery = useMemo(() => debounce(setGrantQuery, 300), []);

  useEffect(() => () => updateGrantQuery.cancel(), [updateGrantQuery]);

  const filteredGrants = useMemo(() => {
    const s = grantQuery.trim().toLowerCase();
    console.log(grants);
    if (!s) return grants;
    return grants.filter((grant) => {
      return `${grant.identity_name} ${grant.identity}`.toLowerCase().includes(s);
    });
  }, [grants, grantQuery]);

  const revokeGrant = async (grant: IamPolicyGrantRecord) => {
    try {
      await revokePolicyGrant.mutateAsync({
        policy_ids: [grant.policy_id],
        identities: [
          {
            identity_type: grant.identity_type,
            identity: grant.identity,
          },
        ],
      });
      message.success('授权已解除');
    } catch {
      // 请求错误由全局请求层统一提示。
    }
  };

  const confirmRemovePolicy = async () => {
    if (!policy || checkingPolicyId) return;

    setCheckingPolicyId(policy.policy_id);
    try {
      const [users, userGroups] = await Promise.all([
        iamHttpApi
          .searchPolicyGrants({
            identity_type: 'user',
            limit: 1,
            offset: '0',
            policy_id: policy.policy_id,
          })
          .then((response) => {
            if (response.code !== 0) {
              throw new Error(response.message || '查询用户策略授权失败');
            }
            return response;
          }),
        iamHttpApi
          .searchPolicyGrants({
            identity_type: 'user_group',
            limit: 1,
            offset: '0',
            policy_id: policy.policy_id,
          })
          .then((response) => {
            if (response.code !== 0) {
              throw new Error(response.message || '查询用户组策略授权失败');
            }
            return response;
          }),
      ]);

      modal.confirm({
        title: '删除策略',
        content: `将解除 ${users.data.total} 个用户、${userGroups.data.total} 个用户组的关联授权，并保留撤销审计记录。`,
        okText: '解除关联并删除',
        okType: 'danger',
        async onOk() {
          try {
            await deletePolicy.mutateAsync(policy.policy_id);
            message.success('策略已删除');
            navigate('/iam/policies', { replace: true });
          } catch {
            // 请求错误由全局请求层统一提示。
          }
        },
      });
    } catch {
      // 点击事件以 fire-and-forget 方式调用，需消费 rejection，错误提示由请求层处理。
    } finally {
      setCheckingPolicyId(undefined);
    }
  };

  if (isPolicyLoading || areGrantsLoading) {
    return (
      <div className="pp-page iam-page iam-policy-detail-page mx-auto w-full max-w-[1200px]">
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }
  if (policyError || !policy) {
    return (
      <div className="pp-page iam-page iam-policy-detail-page mx-auto w-full max-w-[1200px]">
        <Empty
          description={
            policyError
              ? policyError instanceof Error
                ? policyError.message
                : String(policyError)
              : '未找到权限策略'
          }
        />
      </div>
    );
  }

  return (
    <div className="pp-page iam-page iam-policy-page iam-policy-detail-page mx-auto w-full max-w-[1200px]">
      <header className="iam-policy-page-head">
        <Button
          type="text"
          className="iam-policy-detail-back"
          aria-label="返回权限策略列表"
          icon={<ChevronLeft size={16} />}
          onClick={() => navigate('/iam/policies')}
        />
        <div className="iam-policy-page-title">
          <Space size={8} wrap>
            <h2>{policy.name}</h2>
            <Tag bordered={false} color={policy.type === 'system' ? 'blue' : 'purple'}>
              {policy.type === 'system' ? '系统策略' : '自定义策略'}
            </Tag>
          </Space>
          <Typography.Text type="secondary">
            {policy.description || policy.policy_id}
          </Typography.Text>
        </div>
        <Space wrap>
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setGrantDrawerOpen(true)}>
            新增授权
          </Button>
          {policy.type === 'custom' && (
            <Button
              icon={<Edit3 size={14} />}
              onClick={() => navigate(`/iam/policies/${policy.policy_id}/edit`)}
            >
              编辑策略
            </Button>
          )}
          {policy.type === 'custom' && (
            <Button
              danger
              aria-busy={checkingPolicyId === policy.policy_id}
              loading={checkingPolicyId === policy.policy_id}
              icon={<Trash2 size={14} />}
              onClick={() => void confirmRemovePolicy()}
            >
              删除策略
            </Button>
          )}
        </Space>
      </header>

      <div className="iam-policy-page-content">
        <Space direction="vertical" size={16} className="iam-policy-detail-body">
          <Descriptions
            column={1}
            bordered
            size="small"
            items={[
              { key: 'name', label: '策略名', children: policy.name },
              { key: 'desc', label: '描述', children: policy.description || '—' },
              { key: 'count', label: '授权次数', children: `${policy.authorization_count} 次` },
              {
                key: 'createdAt',
                label: '创建时间',
                children: formatPolicyTime(policy.created_at),
              },
            ]}
          />

          <div className="iam-policy-content-head">
            <Typography.Title level={5}>策略内容</Typography.Title>
            <Button onClick={() => setShowSource((value) => !value)}>
              {showSource ? '返回列表' : '源码查看'}
            </Button>
          </div>

          {showSource ? (
            <pre className="iam-policy-source">{JSON.stringify(policy.policy_config, null, 2)}</pre>
          ) : (
            <PolicyPermissionView items={permissionItems} />
          )}

          <div>
            <Typography.Title level={5}>
              被引用的授权记录（{grantsResponse?.data.total ?? grants.length}）
            </Typography.Title>
            <Input
              allowClear
              className="iam-policy-grant-search"
              placeholder="搜索授权主体名称"
              value={grantSearch}
              onChange={(event) => {
                const value = event.target.value;
                setGrantSearch(value);
                updateGrantQuery(value);
              }}
            />
            {grantsError ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  grantsError instanceof Error ? grantsError.message : String(grantsError)
                }
              />
            ) : filteredGrants.length ? (
              <div className="iam-policy-grant-list">
                {filteredGrants.map((grant) => {
                  const openPrincipal = () => {
                    if (grant.identity_type === 'user') {
                      navigate(`/iam/users/${grant.identity}`);
                    } else {
                      navigate(`/iam/groups/${grant.identity}`);
                    }
                  };
                  return (
                    <div
                      className="iam-policy-grant-record"
                      key={`${grant.identity_type}-${grant.identity}-${grant.policy_id}-${grant.authorization_at}`}
                    >
                      <div className="iam-policy-grant-record-main">
                        <Space size={8} wrap>
                          <Typography.Text strong>
                            {grant.identity_name || grant.identity}
                          </Typography.Text>
                          <Tag bordered={false}>
                            {grant.identity_type === 'user' ? '用户' : '用户组'}
                          </Tag>
                        </Space>
                        {grant.identity_type === 'user' && (
                          <Typography.Text type="secondary">{grant.identity}</Typography.Text>
                        )}
                      </div>
                      <div className="iam-policy-grant-record-audit">
                        <Typography.Text style={{ color: 'var(--ant-color-text-description)' }}>
                          {grant.authorizer_name}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {formatPolicyTime(grant.authorization_at)}
                        </Typography.Text>
                      </div>
                      <Space size={0}>
                        <Tooltip title="前往授权主体">
                          <Button
                            type="text"
                            aria-label="前往授权主体"
                            icon={<ExternalLink size={15} />}
                            onClick={openPrincipal}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="解除授权？"
                          description="解除后，授权主体相关用户将不再具备该策略下的全部权限。"
                          okText="确认解除"
                          okButtonProps={{ danger: true, loading: revokePolicyGrant.isPending }}
                          onConfirm={() => revokeGrant(grant)}
                        >
                          <Tooltip title="解除授权">
                            <Button
                              type="text"
                              danger
                              aria-label="解除授权"
                              icon={<ShieldOff size={15} />}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未被授权记录引用" />
            )}
          </div>
        </Space>
      </div>

      <GrantPolicyDrawer
        open={grantDrawerOpen}
        policy={{ id: policy.policy_id, name: policy.name }}
        onClose={() => setGrantDrawerOpen(false)}
      />
    </div>
  );
}
